import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { storeSessionOnServer, initUserManager } from "./AuthProvider";
import useSessionTimer from "../session/hooks/useSessionTimer";
import { getSessionParams } from "../session/utils/sessionStorage";
import { logger } from "../../shared/utils/logger.client";
import { useTranslation } from "react-i18next";

export default function CallbackEidas() {
  const navigate = useNavigate();
  const processedRef = useRef(false);
  const { startTimer } = useSessionTimer();
  const { t } = useTranslation();

  useEffect(() => {
    (async () => {
      if (processedRef.current) return;
      processedRef.current = true;

      sessionStorage.setItem("authCompleting", "1");

      logger.debug("After eIDAS callback, session params:", getSessionParams());

      try {
        const secretToken = sessionStorage.getItem("eidas_secret_token");
        if (!secretToken) throw new Error("No secret token found in sessionStorage");

        sessionStorage.removeItem("eidas_secret_token");

        const secretRes = await fetch("/session/eidas-secret-exchange", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ secretToken }),
        });

        if (!secretRes.ok) {
          const errorText = await secretRes.text().catch(() => "");
          throw new Error(`Failed to exchange secret token: ${secretRes.status} ${errorText}`);
        }

        const { clientSecret } = await secretRes.json();
        if (!clientSecret) throw new Error("No clientSecret returned from eIDAS secret exchange");

        const um = await initUserManager("EIDAS", clientSecret);
        const user = await um.signinRedirectCallback(window.location.href);

        const accessToken = user?.access_token;
        const idToken = user?.id_token;

        if (!accessToken) throw new Error("No access_token received from eIDAS token endpoint");

        await storeSessionOnServer(accessToken, { idToken, loggedMethod: "EIDAS" });
        localStorage.setItem("loggedMethod", "EIDAS");

        await startTimer();
        sessionStorage.removeItem("authCompleting");
        navigate("/preview", { replace: true });
      } catch (err: any) {
        logger.error("Error processing eIDAS callback:", err);
        navigate("/login", { replace: true });
      } finally {
        try {
          sessionStorage.removeItem("authCompleting");
          sessionStorage.removeItem("oidcRedirect");
          sessionStorage.removeItem("oidcRedirectAt");
          localStorage.removeItem("suppressLogoutModal");
        } catch {}
      }
    })();
  }, [navigate, startTimer]);

  return (
    <>
      <style>{`@keyframes spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}`}</style>
      <div
        role="status"
        aria-live="assertive"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
        }}
      >
        <div
          style={{
            background: "#fff",
            padding: "24px 28px",
            borderRadius: 12,
            minWidth: 320,
            textAlign: "center",
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              border: "4px solid #eee",
              borderTop: "4px solid #1976d2",
              animation: "spin 0.9s linear infinite",
              margin: "0 auto",
            }}
            aria-hidden
          />
          <h2 style={{ marginTop: 12, fontSize: 18, fontWeight: 700 }}>{t("callbackEidas.loading.title")}</h2>
          <p style={{ marginTop: 6, fontSize: 13, opacity: 0.8 }}>
            {t("callbackEidas.loading.subtitle")}
          </p>
        </div>
      </div>
    </>
  );
}
